/**
 * Shared onboarding field primitives. Thin wrappers over the shadcn ui
 * components so every onboarding step has consistent labels, help text, and
 * a11y wiring (DES-09: inputs always paired with a real <label>; placeholder
 * is not a substitute).
 */

import type { ReactNode } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface FieldShellProps {
  id: string;
  label: string;
  help?: string;
  children: ReactNode;
}

function FieldShell({ id, label, help, children }: FieldShellProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-medium">
        {label}
      </Label>
      {help && (
        <p
          id={`${id}-help`}
          className="text-xs text-[color:var(--color-text-mute)]"
        >
          {help}
        </p>
      )}
      {children}
    </div>
  );
}

export function TextField({
  id,
  label,
  help,
  value,
  onChange,
  placeholder,
}: {
  id: string;
  label: string;
  help?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <FieldShell id={id} label={label} {...(help !== undefined ? { help } : {})}>
      <Input
        id={id}
        name={id}
        value={value}
        placeholder={placeholder}
        aria-describedby={help ? `${id}-help` : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    </FieldShell>
  );
}

export function LongTextField({
  id,
  label,
  help,
  value,
  onChange,
  placeholder,
  rows = 4,
}: {
  id: string;
  label: string;
  help?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <FieldShell id={id} label={label} {...(help !== undefined ? { help } : {})}>
      <Textarea
        id={id}
        name={id}
        rows={rows}
        value={value}
        placeholder={placeholder}
        aria-describedby={help ? `${id}-help` : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    </FieldShell>
  );
}

/**
 * Comma-separated list field. Stores a string[] in the section; the input is a
 * single line the subscriber types comma-separated values into. Kept simple on
 * purpose — onboarding is not the place for a tag editor.
 */
export function ListField({
  id,
  label,
  help,
  value,
  onChange,
  placeholder,
}: {
  id: string;
  label: string;
  help?: string;
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
}) {
  return (
    <FieldShell
      id={id}
      label={label}
      help={help ?? 'Separate with commas.'}
    >
      <Input
        id={id}
        name={id}
        value={value.join(', ')}
        placeholder={placeholder}
        aria-describedby={`${id}-help`}
        onChange={(e) =>
          onChange(
            e.target.value
              .split(',')
              .map((s) => s.trim())
              .filter((s) => s.length > 0),
          )
        }
      />
    </FieldShell>
  );
}

export function StepHeading({
  eyebrow,
  title,
  intro,
}: {
  eyebrow: string;
  title: string;
  intro?: string;
}) {
  return (
    <header className="mb-8">
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-text-mute)]">
        {eyebrow}
      </p>
      <h1 className="mt-2 font-display text-2xl italic leading-tight text-[color:var(--color-navy)] sm:text-3xl">
        {title}
      </h1>
      {intro && (
        <p className="mt-3 text-[15px] leading-relaxed text-[color:var(--color-text-mute)]">
          {intro}
        </p>
      )}
    </header>
  );
}
